/**
 * ML Intelligence Page - Regulatory Intelligence Dashboard
 * 
 * Implements the "Audit-Ready Pre-Flight Check" and "Regulatory Intelligence" features:
 * - Anomaly Detection (KPI data outliers)
 * - Conflict/Greenwashing Detection
 * - Evidence Freshness & Authenticity
 * - Defensibility Scoring
 * - Regulatory Radar (Horizon Scanning)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Brain, AlertTriangle, Shield, CheckCircle, XCircle, 
  AlertOctagon, Radar, FileCheck, TrendingUp, TrendingDown,
  RefreshCw, ChevronRight, Clock, Target, Zap, Info,
  BarChart3, Layers, ArrowUpRight, ArrowDownRight, Minus,
  LineChart, Activity, Gauge, Lightbulb, Play, Database
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { apiFetch } from '../../services/api_service';


// Severity colors
const SEVERITY_COLORS = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  low: 'bg-green-500/20 text-green-400 border-green-500/30',
};

const STATUS_COLORS = {
  pass: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  warning: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  fail: 'bg-red-500/20 text-red-400 border-red-500/30',
};

export default function MLIntelligencePage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [preflightResult, setPreflightResult] = useState(null);
  const [anomalyResults, setAnomalyResults] = useState(null);
  const [regulatoryUpdates, setRegulatoryUpdates] = useState(null);
  const [runningCheck, setRunningCheck] = useState(false);
  
  // Predictive Analytics state
  const [predictiveData, setPredictiveData] = useState(null);
  const [predictiveAlerts, setPredictiveAlerts] = useState(null);
  const [runningForecast, setRunningForecast] = useState(false);
  const [seedingData, setSeedingData] = useState(false);

  // Get auth token
  const getToken = () => localStorage.getItem('token');

  // Fetch dashboard summary
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await apiFetch(`/v1/ml-intelligence/dashboard-summary`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  }, []);

  // Run pre-flight check
  const runPreflightCheck = async () => {
    setRunningCheck(true);
    try {
      const response = await apiFetch(`/v1/ml-intelligence/preflight-check`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          include_anomaly_detection: true,
          include_conflict_detection: true,
          include_evidence_checks: true,
          include_defensibility_scores: true
        })
      });
      if (response) {
        const data = await response.json();
        setPreflightResult(data);
        fetchDashboard(); // Refresh dashboard after check
      }
    } catch (error) {
      console.error('Failed to run pre-flight check:', error);
    } finally {
      setRunningCheck(false);
    }
  };

  // Run anomaly detection with sample data
  const runAnomalyDetection = async () => {
    try {
      const response = await apiFetch(`/v1/ml-intelligence/anomaly-detection`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          kpi_data: [
            { kpi_id: 'scope_1_emissions', name: 'Scope 1 Emissions', value: 15000, unit: 'tCO2e' },
            { kpi_id: 'scope_2_emissions', name: 'Scope 2 Emissions', value: 8500, unit: 'tCO2e' },
            { kpi_id: 'scope_3_emissions', name: 'Scope 3 Emissions', value: 45000, unit: 'tCO2e' },
            { kpi_id: 'renewable_energy_percentage', name: 'Renewable Energy', value: 65, unit: '%' },
            { kpi_id: 'water_withdrawal', name: 'Water Withdrawal', value: 250000, unit: 'm3' },
            { kpi_id: 'female_employees_percentage', name: 'Female Employees', value: 42, unit: '%' },
            { kpi_id: 'gender_pay_gap', name: 'Gender Pay Gap', value: 8.5, unit: '%' },
            { kpi_id: 'board_gender_diversity', name: 'Board Gender Diversity', value: 35, unit: '%' },
          ],
          historical_periods: 3
        })
      });
      if (response) {
        const data = await response.json();
        setAnomalyResults(data);
      }
    } catch (error) {
      console.error('Failed to run anomaly detection:', error);
    }
  };

  // Scan for regulatory updates
  const scanRegulatoryUpdates = async () => {
    try {
      const response = await apiFetch(`/v1/ml-intelligence/regulatory-radar/scan`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          frameworks: ['CSRD_ESRS', 'EU_TAXONOMY', 'CSDDD', 'SFDR', 'CBAM', 'TCFD', 'GRI']
        })
      });
      if (response) {
        const data = await response.json();
        setRegulatoryUpdates(data);
      }
    } catch (error) {
      console.error('Failed to scan regulatory updates:', error);
    }
  };

  // Seed demo data for predictive analytics
  const seedDemoData = async () => {
    setSeedingData(true);
    try {
      const response = await apiFetch(`/v1/predictive/demo/seed-data`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response) {
        await runQuarterlyForecast();
      }
    } catch (error) {
      console.error('Failed to seed demo data:', error);
    } finally {
      setSeedingData(false);
    }
  };

  // Run quarterly forecast
  const runQuarterlyForecast = async () => {
    setRunningForecast(true);
    try {
      const response = await apiFetch(`/v1/predictive/forecast/quarterly`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      if (response) {
        const data = await response.json();
        setPredictiveData(data);
      }
    } catch (error) {
      console.error('Failed to run forecast:', error);
    } finally {
      setRunningForecast(false);
    }
  };

  // Fetch predictive alerts
  const fetchPredictiveAlerts = async () => {
    try {
      const response = await apiFetch(`/v1/predictive/alerts?limit=20`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response) {
        const data = await response.json();
        setPredictiveAlerts(data);
      }
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        runAnomalyDetection(),
        scanRegulatoryUpdates(),
        fetchPredictiveAlerts()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="text-center">
          <Brain className="w-12 h-12 text-purple-400 mx-auto mb-4 animate-pulse" />
          <p className="text-[#A0A0A0]">Loading ML Intelligence...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-[#F5F5F5]">
      {/* Header */}
      <div className="border-b border-[#2F2F2F] bg-[#1A1A1A]/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Brain className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h1 className="text-xl font-semibold">ML Intelligence</h1>
                <p className="text-sm text-[#6B6B6B]">Regulatory Intelligence & Audit Readiness</p>
              </div>
            </div>
            <Button 
              onClick={runPreflightCheck}
              disabled={runningCheck}
              className="bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 border border-purple-500/30"
            >
              {runningCheck ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Running Check...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Run Pre-Flight Check
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatusCard
            title="Open Anomalies"
            value={dashboardData?.open_anomalies || 0}
            icon={AlertTriangle}
            color="amber"
            description="KPI data issues"
          />
          <StatusCard
            title="Active Conflicts"
            value={dashboardData?.active_conflicts || 0}
            icon={AlertOctagon}
            color="red"
            description="Greenwashing risks"
          />
          <StatusCard
            title="Expiring Evidence"
            value={dashboardData?.expiring_evidence || 0}
            icon={Clock}
            color="orange"
            description="Within 30 days"
          />
          <StatusCard
            title="Regulatory Updates"
            value={regulatoryUpdates?.total_updates || 0}
            icon={Radar}
            color="blue"
            description="Framework changes"
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#1A1A1A] border border-[#2F2F2F] p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#2F2F2F]">
              Overview
            </TabsTrigger>
            <TabsTrigger value="predictive" className="data-[state=active]:bg-[#2F2F2F]">
              <LineChart className="w-4 h-4 mr-1" />
              Predictive
            </TabsTrigger>
            <TabsTrigger value="anomalies" className="data-[state=active]:bg-[#2F2F2F]">
              Anomaly Detection
            </TabsTrigger>
            <TabsTrigger value="conflicts" className="data-[state=active]:bg-[#2F2F2F]">
              Conflict Detection
            </TabsTrigger>
            <TabsTrigger value="regulatory" className="data-[state=active]:bg-[#2F2F2F]">
              Regulatory Radar
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {preflightResult ? (
              <PreFlightResultCard result={preflightResult} />
            ) : (
              <Card className="bg-[#1A1A1A] border-[#2F2F2F]">
                <CardContent className="p-6 text-center">
                  <Shield className="w-12 h-12 text-[#6B6B6B] mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Pre-Flight Check Run</h3>
                  <p className="text-[#6B6B6B] mb-4">
                    Run a comprehensive pre-flight check to assess your audit readiness
                  </p>
                  <Button 
                    onClick={runPreflightCheck}
                    disabled={runningCheck}
                    className="bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 border border-purple-500/30"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Start Pre-Flight Check
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Feature Status */}
            <Card className="bg-[#1A1A1A] border-[#2F2F2F]">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-purple-400" />
                  ML Intelligence Features
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {Object.entries(dashboardData?.features_status || {}).map(([feature, status]) => (
                    <div key={feature} className="flex items-center gap-2 p-3 bg-[#0D0D0D] rounded-lg border border-[#2F2F2F]">
                      <div className={`w-2 h-2 rounded-full ${status === 'active' ? 'bg-emerald-400' : 'bg-[#6B6B6B]'}`} />
                      <span className="text-sm capitalize">{feature.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Predictive Analytics Tab */}
          <TabsContent value="predictive" className="space-y-6">
            <Card className="bg-[#1A1A1A] border-[#2F2F2F]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <LineChart className="w-5 h-5 text-cyan-400" />
                      KPI Forecasting & Predictive Analytics
                    </CardTitle>
                    <CardDescription>
                      ML-powered predictions for compliance risk and KPI trends
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {!predictiveData && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={seedDemoData}
                        disabled={seedingData}
                        className="border-[#2F2F2F]"
                      >
                        {seedingData ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Database className="w-4 h-4 mr-2" />
                        )}
                        Seed Demo Data
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={runQuarterlyForecast}
                      disabled={runningForecast}
                      className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                    >
                      {runningForecast ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4 mr-2" />
                      )}
                      Run Forecast
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {predictiveData ? (
                  <div className="space-y-6">
                    {/* Executive Summary */}
                    <div className="p-4 bg-[#0D0D0D] rounded-lg border border-[#2F2F2F]">
                      <h4 className="text-sm font-medium text-[#A0A0A0] mb-2">Executive Summary</h4>
                      <p className="text-sm text-[#F5F5F5]">{predictiveData.executive_summary}</p>
                    </div>

                    {/* Risk Score Overview */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-[#0D0D0D] rounded-lg border border-[#2F2F2F]">
                        <div className="flex items-center gap-2 mb-2">
                          <Gauge className="w-4 h-4 text-cyan-400" />
                          <span className="text-sm text-[#6B6B6B]">Overall Risk</span>
                        </div>
                        <div className="text-2xl font-bold text-[#F5F5F5]">
                          {predictiveData.compliance_risk?.overall_score?.toFixed(0) || 0}
                          <span className="text-sm text-[#6B6B6B] ml-1">/100</span>
                        </div>
                        <Progress value={predictiveData.compliance_risk?.overall_score || 0} className="h-1 mt-2" />
                      </div>
                      
                      <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                        <div className="flex items-center gap-2 mb-2">
                          <Activity className="w-4 h-4 text-emerald-400" />
                          <span className="text-sm text-emerald-400">Environmental</span>
                        </div>
                        <div className="text-2xl font-bold text-emerald-400">
                          {predictiveData.compliance_risk?.category_scores?.environmental?.toFixed(0) || 0}
                        </div>
                      </div>
                      
                      <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                        <div className="flex items-center gap-2 mb-2">
                          <Activity className="w-4 h-4 text-blue-400" />
                          <span className="text-sm text-blue-400">Social</span>
                        </div>
                        <div className="text-2xl font-bold text-blue-400">
                          {predictiveData.compliance_risk?.category_scores?.social?.toFixed(0) || 0}
                        </div>
                      </div>
                      
                      <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                        <div className="flex items-center gap-2 mb-2">
                          <Activity className="w-4 h-4 text-purple-400" />
                          <span className="text-sm text-purple-400">Governance</span>
                        </div>
                        <div className="text-2xl font-bold text-purple-400">
                          {predictiveData.compliance_risk?.category_scores?.governance?.toFixed(0) || 0}
                        </div>
                      </div>
                    </div>

                    {/* KPI Forecasts */}
                    {predictiveData.kpi_forecasts?.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-3 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-cyan-400" />
                          KPI Forecasts ({predictiveData.total_kpis} KPIs)
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {predictiveData.kpi_forecasts.slice(0, 6).map((forecast, idx) => (
                            <KPIForecastCard key={idx} forecast={forecast} />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Alerts */}
                    {predictiveData.alerts?.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-3 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-400" />
                          Proactive Alerts ({predictiveData.total_alerts})
                        </h4>
                        <div className="space-y-3">
                          {predictiveData.alerts.slice(0, 5).map((alert, idx) => (
                            <PredictiveAlertCard key={idx} alert={alert} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <LineChart className="w-12 h-12 text-[#6B6B6B] mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Forecast Data</h3>
                    <p className="text-[#6B6B6B] mb-4">
                      Seed demo data and run a quarterly forecast to see predictions
                    </p>
                    <Button 
                      onClick={seedDemoData}
                      disabled={seedingData}
                      className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border border-cyan-500/30"
                    >
                      <Database className="w-4 h-4 mr-2" />
                      Get Started with Demo Data
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Anomaly Detection Tab */}
          <TabsContent value="anomalies" className="space-y-6">
            <Card className="bg-[#1A1A1A] border-[#2F2F2F]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                      Anomaly Detection Results
                    </CardTitle>
                    <CardDescription>
                      Statistical analysis of ESG KPIs for outliers and unexpected values
                    </CardDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={runAnomalyDetection}
                    className="border-[#2F2F2F]"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {anomalyResults ? (
                  <div className="space-y-4">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-4 gap-4 p-4 bg-[#0D0D0D] rounded-lg border border-[#2F2F2F]">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#F5F5F5]">{anomalyResults.total_kpis_analyzed}</div>
                        <div className="text-sm text-[#6B6B6B]">KPIs Analyzed</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-400">{anomalyResults.critical_count}</div>
                        <div className="text-sm text-[#6B6B6B]">Critical</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-orange-400">{anomalyResults.high_count}</div>
                        <div className="text-sm text-[#6B6B6B]">High</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-yellow-400">{anomalyResults.medium_count}</div>
                        <div className="text-sm text-[#6B6B6B]">Medium</div>
                      </div>
                    </div>

                    {/* KPI Results */}
                    <div className="space-y-3">
                      {anomalyResults.results.map((result, idx) => (
                        <AnomalyResultCard key={idx} result={result} />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-[#6B6B6B]">
                    No anomaly detection results available
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Conflict Detection Tab */}
          <TabsContent value="conflicts" className="space-y-6">
            <Card className="bg-[#1A1A1A] border-[#2F2F2F]">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertOctagon className="w-5 h-5 text-red-400" />
                  Conflict & Greenwashing Detection
                </CardTitle>
                <CardDescription>
                  AI-powered analysis for claim contradictions and greenwashing risks
                </CardDescription>
              </CardHeader>
              <CardContent>
                {preflightResult?.details?.conflicts?.length > 0 ? (
                  <div className="space-y-3">
                    {preflightResult.details.conflicts.map((conflict, idx) => (
                      <ConflictCard key={idx} conflict={conflict} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Conflicts Detected</h3>
                    <p className="text-[#6B6B6B]">
                      Run a pre-flight check to analyze claims for conflicts
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Regulatory Radar Tab */}
          <TabsContent value="regulatory" className="space-y-6">
            <Card className="bg-[#1A1A1A] border-[#2F2F2F]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Radar className="w-5 h-5 text-blue-400" />
                      Regulatory Radar
                    </CardTitle>
                    <CardDescription>
                      Framework updates and horizon scanning for regulatory changes
                    </CardDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={scanRegulatoryUpdates}
                    className="border-[#2F2F2F]"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Scan Now
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {regulatoryUpdates?.updates?.length > 0 ? (
                  <div className="space-y-4">
                    {regulatoryUpdates.updates.map((update, idx) => (
                      <RegulatoryUpdateCard key={idx} update={update} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-[#6B6B6B]">
                    No regulatory updates available
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// Status Card Component
function StatusCard({ title, value, icon: Icon, color, description }) {
  const colorClasses = {
    amber: 'text-amber-400 bg-amber-500/20',
    red: 'text-red-400 bg-red-500/20',
    orange: 'text-orange-400 bg-orange-500/20',
    blue: 'text-blue-400 bg-blue-500/20',
    green: 'text-emerald-400 bg-emerald-500/20',
  };

  return (
    <Card className="bg-[#1A1A1A] border-[#2F2F2F]">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-4 h-4" />
          </div>
          <span className="text-2xl font-bold">{value}</span>
        </div>
        <h3 className="font-medium text-sm">{title}</h3>
        <p className="text-xs text-[#6B6B6B]">{description}</p>
      </CardContent>
    </Card>
  );
}

// Pre-Flight Result Card
function PreFlightResultCard({ result }) {
  const statusIcon = {
    pass: <CheckCircle className="w-6 h-6 text-emerald-400" />,
    warning: <AlertTriangle className="w-6 h-6 text-amber-400" />,
    fail: <XCircle className="w-6 h-6 text-red-400" />,
  };

  return (
    <Card className="bg-[#1A1A1A] border-[#2F2F2F]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {statusIcon[result.overall_status]}
            <div>
              <CardTitle className="text-lg">Pre-Flight Check Result</CardTitle>
              <CardDescription>
                {new Date(result.check_timestamp).toLocaleString()}
              </CardDescription>
            </div>
          </div>
          <Badge className={STATUS_COLORS[result.overall_status]}>
            {result.overall_status.toUpperCase()}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Score */}
        <div className="flex items-center gap-4 p-4 bg-[#0D0D0D] rounded-lg border border-[#2F2F2F]">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#6B6B6B]">Overall Score</span>
              <span className="text-lg font-bold">{result.overall_score}/100</span>
            </div>
            <Progress value={result.overall_score} className="h-2" />
          </div>
        </div>

        {/* Summary */}
        <div className="p-4 bg-[#0D0D0D] rounded-lg border border-[#2F2F2F]">
          <pre className="text-sm whitespace-pre-wrap font-mono text-[#A0A0A0]">
            {result.executive_summary}
          </pre>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-4">
          <div className="p-3 bg-[#0D0D0D] rounded-lg border border-[#2F2F2F] text-center">
            <div className="text-xl font-bold text-[#F5F5F5]">{result.claims_reviewed}</div>
            <div className="text-xs text-[#6B6B6B]">Claims Reviewed</div>
          </div>
          <div className="p-3 bg-[#0D0D0D] rounded-lg border border-amber-500/30 text-center">
            <div className="text-xl font-bold text-amber-400">{result.anomalies_detected}</div>
            <div className="text-xs text-[#6B6B6B]">Anomalies</div>
          </div>
          <div className="p-3 bg-[#0D0D0D] rounded-lg border border-red-500/30 text-center">
            <div className="text-xl font-bold text-red-400">{result.conflicts_detected}</div>
            <div className="text-xs text-[#6B6B6B]">Conflicts</div>
          </div>
          <div className="p-3 bg-[#0D0D0D] rounded-lg border border-orange-500/30 text-center">
            <div className="text-xl font-bold text-orange-400">{result.evidence_issues}</div>
            <div className="text-xs text-[#6B6B6B]">Evidence Issues</div>
          </div>
        </div>

        {/* Priority Actions */}
        {result.priority_actions?.length > 0 && (
          <div>
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-purple-400" />
              Priority Actions
            </h4>
            <div className="space-y-2">
              {result.priority_actions.slice(0, 5).map((action, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center gap-3 p-3 bg-[#0D0D0D] rounded-lg border border-[#2F2F2F]"
                >
                  <span className={`w-6 h-6 flex items-center justify-center rounded text-xs font-bold ${
                    action.priority === 1 ? 'bg-red-500/20 text-red-400' :
                    action.priority === 2 ? 'bg-orange-500/20 text-orange-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {action.priority}
                  </span>
                  <div className="flex-1">
                    <p className="text-sm">{action.action}</p>
                    <p className="text-xs text-[#6B6B6B]">{action.impact}</p>
                  </div>
                  <Badge variant="outline" className="border-[#2F2F2F] text-xs">
                    {action.type}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Anomaly Result Card
function AnomalyResultCard({ result }) {
  const getTrendIcon = () => {
    if (result.yoy_change_percent > 0) return <ArrowUpRight className="w-4 h-4 text-red-400" />;
    if (result.yoy_change_percent < 0) return <ArrowDownRight className="w-4 h-4 text-emerald-400" />;
    return <Minus className="w-4 h-4 text-[#6B6B6B]" />;
  };

  return (
    <div className={`p-4 rounded-lg border ${
      result.anomaly_detected 
        ? SEVERITY_COLORS[result.severity]
        : 'bg-[#0D0D0D] border-[#2F2F2F]'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          {result.anomaly_detected ? (
            <AlertTriangle className={`w-5 h-5 ${
              result.severity === 'critical' ? 'text-red-400' :
              result.severity === 'high' ? 'text-orange-400' :
              'text-yellow-400'
            }`} />
          ) : (
            <CheckCircle className="w-5 h-5 text-emerald-400" />
          )}
          <div>
            <h4 className="font-medium">{result.kpi_name}</h4>
            <p className="text-xs text-[#6B6B6B]">{result.category}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold">{result.current_value.toLocaleString()}</span>
            <span className="text-sm text-[#6B6B6B]">{result.unit}</span>
          </div>
          {result.yoy_change_percent !== null && (
            <div className="flex items-center gap-1 justify-end">
              {getTrendIcon()}
              <span className="text-xs">{result.yoy_change_percent?.toFixed(1)}% YoY</span>
            </div>
          )}
        </div>
      </div>
      <p className="text-sm text-[#A0A0A0] mb-2">{result.description}</p>
      {result.anomaly_detected && (
        <div className="flex items-center gap-2 text-xs">
          <Info className="w-3 h-3" />
          <span>{result.recommendation}</span>
        </div>
      )}
    </div>
  );
}

// Conflict Card
function ConflictCard({ conflict }) {
  return (
    <div className={`p-4 rounded-lg border ${SEVERITY_COLORS[conflict.severity]}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <AlertOctagon className="w-5 h-5" />
          <span className="font-medium capitalize">{conflict.conflict_type.replace(/_/g, ' ')}</span>
        </div>
        <Badge className={SEVERITY_COLORS[conflict.severity]}>
          {conflict.severity.toUpperCase()}
        </Badge>
      </div>
      <p className="text-sm mb-2">{conflict.description}</p>
      <div className="flex items-center justify-between text-xs">
        <span className="text-[#6B6B6B]">Greenwashing Risk: {conflict.greenwashing_risk_score}%</span>
        {conflict.resolution_required && (
          <Badge variant="outline" className="border-red-500/30 text-red-400">
            Resolution Required
          </Badge>
        )}
      </div>
      <div className="mt-2 p-2 bg-black/20 rounded text-xs">
        <span className="text-[#6B6B6B]">Recommendation:</span> {conflict.recommendation}
      </div>
    </div>
  );
}

// Regulatory Update Card
function RegulatoryUpdateCard({ update }) {
  const typeColors = {
    new_requirement: 'bg-purple-500/20 text-purple-400',
    amendment: 'bg-blue-500/20 text-blue-400',
    deadline_change: 'bg-amber-500/20 text-amber-400',
    guidance: 'bg-emerald-500/20 text-emerald-400',
  };

  return (
    <div className="p-4 bg-[#0D0D0D] rounded-lg border border-[#2F2F2F]">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Badge className={typeColors[update.update_type] || 'bg-[#2F2F2F]'}>
            {update.update_type.replace(/_/g, ' ')}
          </Badge>
          <span className="font-medium">{update.framework_name}</span>
        </div>
        {update.effective_date && (
          <span className="text-xs text-[#6B6B6B]">
            Effective: {new Date(update.effective_date).toLocaleDateString()}
          </span>
        )}
      </div>
      <p className="text-sm mb-2">{update.description}</p>
      <div className="p-2 bg-[#1A1A1A] rounded text-sm mb-2">
        <span className="text-[#6B6B6B]">Impact:</span> {update.impact_assessment}
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-purple-400" />
          <span className="text-sm">{update.action_required}</span>
        </div>
        {update.source_url && (
          <a 
            href={update.source_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-blue-400 hover:underline flex items-center gap-1"
          >
            Source <ChevronRight className="w-3 h-3" />
          </a>
        )}
      </div>
    </div>
  );
}

// KPI Forecast Card
function KPIForecastCard({ forecast }) {
  const getRiskColor = (level) => {
    const colors = {
      critical: 'border-red-500/30 bg-red-500/10',
      high: 'border-orange-500/30 bg-orange-500/10',
      medium: 'border-yellow-500/30 bg-yellow-500/10',
      low: 'border-emerald-500/30 bg-emerald-500/10',
    };
    return colors[level] || 'border-[#2F2F2F] bg-[#0D0D0D]';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingDown className="w-4 h-4 text-emerald-400" />;
    if (trend === 'declining') return <TrendingUp className="w-4 h-4 text-red-400" />;
    if (trend === 'volatile') return <Activity className="w-4 h-4 text-amber-400" />;
    return <Minus className="w-4 h-4 text-[#6B6B6B]" />;
  };

  return (
    <div className={`p-4 rounded-lg border ${getRiskColor(forecast.risk_level)}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {getTrendIcon(forecast.trend)}
          <span className="font-medium text-sm">{forecast.kpi_name || forecast.kpi_id}</span>
        </div>
        <Badge className={SEVERITY_COLORS[forecast.risk_level] || 'bg-[#2F2F2F]'}>
          {forecast.risk_level}
        </Badge>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <div className="text-xs text-[#6B6B6B] mb-1">Current</div>
          <div className="text-lg font-bold">
            {forecast.current_value?.toLocaleString()}
            <span className="text-xs text-[#6B6B6B] ml-1">{forecast.unit}</span>
          </div>
        </div>
        <div>
          <div className="text-xs text-[#6B6B6B] mb-1">Predicted</div>
          <div className="text-lg font-bold text-cyan-400">
            {forecast.predicted_value?.toLocaleString()}
            <span className="text-xs text-[#6B6B6B] ml-1">{forecast.unit}</span>
          </div>
        </div>
      </div>
      
      {forecast.confidence_interval && (
        <div className="text-xs text-[#6B6B6B]">
          95% CI: [{forecast.confidence_interval.lower?.toLocaleString()} - {forecast.confidence_interval.upper?.toLocaleString()}]
        </div>
      )}
      
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#2F2F2F]">
        <span className="text-xs text-[#6B6B6B] capitalize">{forecast.trend?.replace(/_/g, ' ')}</span>
        <span className="text-xs text-[#6B6B6B]">
          Risk: {forecast.risk_score?.toFixed(0)}/100
        </span>
      </div>
    </div>
  );
}

// Predictive Alert Card
function PredictiveAlertCard({ alert }) {
  const severityIcons = {
    critical: <AlertOctagon className="w-5 h-5 text-red-400" />,
    high: <AlertTriangle className="w-5 h-5 text-orange-400" />,
    medium: <Info className="w-5 h-5 text-yellow-400" />,
    low: <Lightbulb className="w-5 h-5 text-emerald-400" />,
  };

  return (
    <div className={`p-4 rounded-lg border ${SEVERITY_COLORS[alert.severity] || 'bg-[#0D0D0D] border-[#2F2F2F]'}`}>
      <div className="flex items-start gap-3">
        {severityIcons[alert.severity] || severityIcons.medium}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium text-sm">{alert.kpi_name || alert.kpi_id}</span>
            <Badge className={SEVERITY_COLORS[alert.severity]}>
              {alert.severity?.toUpperCase()}
            </Badge>
          </div>
          <p className="text-sm text-[#A0A0A0] mb-2">{alert.message}</p>
          {alert.recommendation && (
            <div className="flex items-center gap-2 text-xs">
              <Lightbulb className="w-3 h-3 text-cyan-400" />
              <span className="text-cyan-400">{alert.recommendation}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
